import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, FolderLock, FolderPlus, Loader, Search, X } from 'lucide-react';
import { LogbookActionBadge } from './LogbookBadges';
import { LogbookActor } from './LogbookActor';
import { SkeletonLoader } from '../SkeletonLoader';
import { logbookApiService, LogbookApiError } from '../../services/logbookApi';
import { formatLocalDateTime } from '../../utils/timestamps';
import { cn } from '../../utils/cn';
import { actionMeta, dimensionLabel, formatCoordinates, formatCount, hasKnownTime, relativeTime } from '../../utils/logbook';
import type { LogbookAttachResult, LogbookCase, LogbookEvent } from '../../types/logbook';

interface AddToCaseModalProps {
  /** One record, or a whole grouped run. Up to fifty go in one call. */
  events: LogbookEvent[];
  /** Skips the picker when the case is already known, as on a case page. */
  caseId?: string;
  onClose: () => void;
  onAttached: (result: LogbookAttachResult, caseRef: { id: string; reference: string; title: string }) => void;
}

const MAX_PER_ATTACH = 50;

/**
 * Copies evidence into a case.
 *
 * The records travel with the request, straight from the page that is displaying them, so this works
 * with the evidence service down and cannot half-succeed. A ban raised months later still has the
 * evidence it was based on, whatever central retention has done since.
 */
export const AddToCaseModal = ({ events, caseId, onClose, onAttached }: AddToCaseModalProps) => {
  const [mode, setMode] = useState<'existing' | 'new'>(caseId ? 'existing' : 'existing');
  const [cases, setCases] = useState<LogbookCase[] | null>(null);
  const [selected, setSelected] = useState<string | null>(caseId ?? null);
  const [query, setQuery] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const batch = useMemo(() => events.slice(0, MAX_PER_ATTACH), [events]);
  const overflow = events.length - batch.length;
  const lead = batch[0];

  useEffect(() => {
    if (caseId) return;
    const controller = new AbortController();
    logbookApiService.listCases({ status: 'open', limit: 100 }, controller.signal)
      .then((result) => {
        setCases(result.cases);
        setSelected((current) => current ?? result.cases[0]?.id ?? null);
        // With no open case to attach to, opening one is the only sensible next step.
        if (result.cases.length === 0) setMode('new');
      })
      .catch(() => setCases([]));
    return () => controller.abort();
  }, [caseId]);

  useEffect(() => {
    const onKeyDown = (keyEvent: KeyboardEvent) => { if (keyEvent.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!cases) return [];
    return needle
      ? cases.filter((entry) => `${entry.title} ${entry.reference}`.toLowerCase().includes(needle))
      : cases;
  }, [cases, query]);

  const valid = mode === 'new' ? title.trim().length > 0 : Boolean(selected);

  const submit = useCallback(async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (mode === 'new') {
        const created = await logbookApiService.createCase({
          title: title.trim(),
          records: batch,
          // The actor on the evidence is the obvious first suspect, but only when it is a real
          // player: an unattributed or automated record names nobody.
          subjects: lead && lead.actor_kind === 'player' && lead.actor_id
            ? [{ uuid: lead.actor_id, role: 'suspect' }]
            : undefined,
        });
        onAttached(created.attach ?? { attached: batch.length, duplicates: 0 }, created);
      } else {
        const target = cases?.find((entry) => entry.id === selected);
        const result = await logbookApiService.attachRecords(selected!, batch, note.trim() || undefined);
        onAttached(result, target ?? { id: selected!, reference: selected!, title: 'this case' });
      }
      onClose();
    } catch (caught) {
      setError(caught instanceof LogbookApiError ? caught.message : 'That evidence could not be attached. Please retry.');
      setSaving(false);
    }
  }, [valid, saving, batch, mode, title, note, selected, cases, lead, onAttached, onClose]);

  return (
    <div className="modal-overlay z-[9999]">
      <div className="modal-panel max-w-xl max-md:max-w-[95vw]">
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{batch.length === 1 ? 'Add evidence to a case' : `Add ${formatCount(batch.length)} records to a case`}</h2>
          </div>
          <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-4">
          {/* What is being copied, so the wrong run is not attached by accident */}
          {lead && (
            <div className="subcard p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <LogbookActionBadge action={lead.action} />
                <LogbookActor actorId={lead.actor_id} actorName={lead.actor_name} actorKind={lead.actor_kind} />
                {batch.length > 1 && (
                  <span className="badge badge-neutral">{formatCount(batch.length)} records</span>
                )}
              </div>
              <div className="text-xs text-glass-400 tabular-nums">
                {dimensionLabel(lead.dimension)} {formatCoordinates(lead)} &middot; {lead.server_id}
                {hasKnownTime(lead) && <> &middot; {formatLocalDateTime(new Date(lead.timestamp))}</>}
              </div>
              {batch.length > 1 && (
                <div className="text-xs text-glass-500">
                  {actionMeta(lead.action).label} by the same actor, from this run.
                </div>
              )}
            </div>
          )}

          {overflow > 0 && (
            <div className="alert alert-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                This run holds {formatCount(events.length)} records. The first {MAX_PER_ATTACH} are attached now; repeat
                for the rest.
              </span>
            </div>
          )}

          {!caseId && (
            <div className="segmented">
              <button type="button" className="segmented-item" data-active={mode === 'existing'} onClick={() => setMode('existing')}>
                <FolderLock className="w-4 h-4" />
                Existing case
              </button>
              <button type="button" className="segmented-item" data-active={mode === 'new'} onClick={() => setMode('new')}>
                <FolderPlus className="w-4 h-4" />
                New case
              </button>
            </div>
          )}

          {mode === 'existing' && !caseId && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  type="text"
                  value={query}
                  onChange={(changed) => setQuery(changed.target.value)}
                  placeholder="Find an open case"
                  className="form-input pl-9"
                  aria-label="Find an open case"
                />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {cases === null ? (
                  Array.from({ length: 3 }, (_, index) => <SkeletonLoader key={index} className="h-12 w-full rounded-lg" />)
                ) : matching.length === 0 ? (
                  <p className="text-sm text-glass-500 py-3 text-center">
                    {cases.length === 0 ? 'No case is open yet.' : 'No open case matches that.'}
                  </p>
                ) : matching.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setSelected(entry.id)}
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-lg border transition-colors',
                      entry.id === selected ? 'bg-accent-500/15 border-accent-400/40' : 'border-transparent hover:bg-glass-200/[0.06]',
                    )}
                  >
                    <div className="text-sm text-white truncate">{entry.title}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-glass-500 tabular-nums">
                      <span className="font-mono">{entry.reference}</span>
                      <span aria-hidden>&middot;</span>
                      <span>{formatCount(entry.record_count)} records</span>
                      <span aria-hidden>&middot;</span>
                      <span>{relativeTime(entry.updated_at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === 'new' && (
            <label className="block">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Title</span>
              <input
                type="text"
                value={title}
                onChange={(changed) => setTitle(changed.target.value)}
                className="form-input"
                placeholder="Spawn area griefed overnight"
                maxLength={200}
                autoFocus
              />
            </label>
          )}

          {mode === 'existing' && (
            <label className="block">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Note</span>
              <textarea
                value={note}
                onChange={(changed) => setNote(changed.target.value)}
                className="form-input min-h-[64px] resize-y"
                placeholder="Optional"
                maxLength={500}
              />
            </label>
          )}

          {error && (
            <div className="alert alert-danger">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="glass-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!valid || saving}>
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <FolderLock className="w-4 h-4" />}
            {mode === 'new' ? 'Open case' : 'Add to case'}
          </button>
        </div>
      </div>
    </div>
  );
};
